import { ProductType, SchemaTypes } from '@allesauto/graphql';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useCallback, useMemo } from 'react';

import { pushAnalyticsEvent } from './analytics';
import { VehicleTracking } from './interfaces';

export const VIEW_ITEM_LIST_EVENT = 'view_item_list';
export const SELECT_ITEM_EVENT = 'select_item';
export const RELATED_PRODUCTS_ID = 'related_products';

const itemsTracking: VehicleTracking[] = [];

export const handleTrackingEvent = (
  eventName: string,
  itemListName: string,
  itemListId: string,
  item?: VehicleTracking
) => {
  const { makeName, modelName, id, price, vehicleCondition, bodyColor } =
    item ?? {};

  pushAnalyticsEvent({
    event: eventName,
    ecommerce: {
      items:
        eventName === 'view_item_list'
          ? itemsTracking.map((el, index) => {
              return {
                item_name: `${el.makeName?.toLocaleLowerCase()} ${el.modelName?.toLocaleLowerCase()}`,
                item_id: el.id,
                price: el.price,
                item_brand: el.makeName?.toLocaleLowerCase(),
                item_category: 'car',
                item_category2: el.vehicleCondition?.toLocaleLowerCase(),
                item_variant: el.bodyColor?.toLocaleLowerCase(),
                item_list_name: itemListName,
                item_list_id: itemListId,
                index: index,
                quantity: 1,
              };
            })
          : [
              {
                item_name: `${makeName?.toLocaleLowerCase()} ${modelName?.toLocaleLowerCase()}`,
                item_id: id,
                price: price,
                item_brand: makeName?.toLocaleLowerCase(),
                item_category: 'car',
                item_category2: vehicleCondition?.toLocaleLowerCase(),
                item_variant: bodyColor?.toLocaleLowerCase(),
                item_list_name: itemListName,
                item_list_id: itemListId,
                index: 0,
                quantity: 1,
              },
            ],
    },
  });
};

export const useProductTracking = (
  item: SchemaTypes.Vehicle,
  item_list_id: string,
  item_list_name: string,
  numberOfItemsRow: number
) => {
  const { makeName, modelName, id, price, vehicleCondition, bodyColor } =
    item ?? {};
  const dataTrackingItem = useMemo(() => {
    return {
      makeName: makeName,
      modelName: modelName,
      id: id,
      price: price,
      vehicleCondition: vehicleCondition,
      bodyColor: bodyColor,
    };
  }, [bodyColor, id, makeName, modelName, price, vehicleCondition]);
  const triggerTracking = useCallback(
    (id: string) => {
      if (id === dataTrackingItem.id) {
        itemsTracking.push(dataTrackingItem);
      }
      if (itemsTracking.length && itemsTracking.length === numberOfItemsRow) {
        handleTrackingEvent(VIEW_ITEM_LIST_EVENT, item_list_id, item_list_name);
        itemsTracking.length = 0;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item, numberOfItemsRow]
  );

  return triggerTracking;
};

/**
 * We need to send to the data layer an event for each shipping type chosen
 * with an array of cars for that shipping type
 */
export const groupCartItemsByShippingType = (
  cartItems: SchemaTypes.CartItem[]
) => {
  type ShippingGroups = {
    type: string;
    groupValue: number;
    vehicles: SchemaTypes.CartItem[];
    accessories: SchemaTypes.CartItem[];
  };

  const shippingGroups: ShippingGroups[] = [];

  // Set the types of shipment selected on the current cart
  const shipmentItemsOnCart = cartItems.filter(
    (i) => i.productType === ProductType.Shipment
  );

  // Set the types of shipment selected on the current cart
  shipmentItemsOnCart.map((i) => {
    const shipment = i.product as SchemaTypes.Shipment;

    if (!shippingGroups.some((s) => s.type === shipment.shippingType)) {
      shippingGroups.push({
        type: shipment.shippingType,
        groupValue: 0,
        vehicles: [],
        accessories: [],
      });
    }
  });

  // Sort vehicles and accessories into the different shipping groups
  cartItems.forEach((i) => {
    let useSku: Maybe<string> | null = null;

    if (i.productType === ProductType.Vehicle) useSku = i.sku;
    else if (i.productType === ProductType.Accessory)
      useSku = i.relatedProductSku;

    // If there is no sku store, means that the loop went through a shipping item and we
    // want to skip it
    if (!useSku) {
      return;
    }

    const relatedShipmentItem = shipmentItemsOnCart.find(
      (shipment) =>
        shipment.productType === ProductType.Shipment &&
        shipment.relatedProductSku === useSku
    );

    const shipment = relatedShipmentItem?.product as SchemaTypes.Shipment;

    shippingGroups.forEach((s) => {
      if (s.type === shipment.shippingType) {
        if (i.productType === ProductType.Vehicle) s.vehicles.push(i);
        else if (i.productType === ProductType.Accessory) s.accessories.push(i);
        s.groupValue += i.payment.price ?? 0;
      }
    });
  });

  return shippingGroups;
};

/**
 * We need to send to the data layer an event for each payment type chosen
 * with an array of items for that payment type
 */
export const groupCartItemsByPaymentType = (
  cartItems: SchemaTypes.CartItem[]
) => {
  type PaymentGroups = {
    type: string;
    groupValue: number;
    vehicles: SchemaTypes.CartItem[];
    accessories: SchemaTypes.CartItem[];
  };

  const paymentGroups: PaymentGroups[] = [];

  // Set the types of payment selected on the current cart
  cartItems.map((i) => {
    if (
      i.productType === ProductType.Vehicle ||
      i.productType === ProductType.Accessory
    ) {
      const payment: SchemaTypes.CartItemPayment = i.payment;

      // Add new payment group if it didn't exist
      if (!paymentGroups.some((p) => p.type === payment.paymentType)) {
        if (payment.paymentType) {
          paymentGroups.push({
            type: payment.paymentType,
            groupValue: 0,
            vehicles: [],
            accessories: [],
          });
        }
      }

      // Add items to payment groups
      paymentGroups.forEach((p) => {
        if (p.type === payment.paymentType) {
          if (i.productType === ProductType.Vehicle) p.vehicles.push(i);
          else if (i.productType === ProductType.Accessory)
            p.accessories.push(i);
          p.groupValue += i.payment.price ?? 0;
        }
      });
    }
  });

  return paymentGroups;
};
